import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const IconSize = 35;

const getShadow = (st: number) => {
  if (st >= 6) return 80;
  if (st === 5) return 75;
  if (st === 4) return 70;
  return 50;
};

type WrapperProps = {
  size: string;
  isInjured: number;
  st: number;
};

const Wrapper = styled.div<WrapperProps>`
  height: ${(props) => props.size};
  position: relative;
  width: ${(props) => props.size};
  &::before {
    background-color: ${(props) =>
    props.isInjured === 2 ? "rgba(200, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.7)"};
    border-radius: 50%;
    bottom: ${(props) => (props.isInjured === 0 ? "-2%" : "23%")};
    content: "";
    height: 20%;
    left: 0;
    margin-left: auto;
    margin-right: auto;
    position: absolute;
    right: 0;
    text-align: center;
    width: ${(props) =>
    props.isInjured === 0 ? `${getShadow(props.st)}%` : "70%"};
    z-index: 0;
  }
`;

const Canvas = styled.canvas`
  aspect-ratio: 1;
  height: 100%;
  image-rendering: pixelated;
  position: relative;
  width: 100%;
  z-index: 1;
`;

type IconPlayerExternalProps = {
  careerId: string;
  /** 0 = standing (face), 1 = knocked down (side rotated), 2 = stunned (side rotated + flipped) */
  x: 0 | 1 | 2;
  color?: string;
  size?: string;
  st?: number;
  onError?: () => void;
};

function IconPlayerExternal({
  careerId,
  x,
  color = "#fe0000",
  size = `${IconSize}px`,
  st = 4,
  onError,
}: IconPlayerExternalProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const img = new Image();
    img.src = `/images/players/external/${careerId}/variant-0.png`;

    img.onload = () => {
      // Image is 70x35: col 0 = face (standing), col 1 = side (lying)
      let decalageX = x;
      if (x === 2) decalageX = 1; // stunned reuses side column

      canvas.width = IconSize;
      canvas.height = IconSize;

      // Step 1: pre-process source cell on a temp canvas (no rotation yet)
      // so that shadow removal always targets the correct bottom of the original image
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = IconSize;
      tempCanvas.height = IconSize;
      const tempCtx = tempCanvas.getContext("2d", { willReadFrequently: true });
      if (!tempCtx) return;
      tempCtx.drawImage(img, -(decalageX * IconSize), 0);

      const imageData = tempCtx.getImageData(0, 0, IconSize, IconSize);
      const data = imageData.data;
      const hex = color.replace("#", "");
      const cr = parseInt(hex.substring(0, 2), 16);
      const cg = parseInt(hex.substring(2, 4), 16);
      const cb = parseInt(hex.substring(4, 6), 16);
      const shadowStartY = Math.floor(IconSize * 0.75);
      for (let py = 0; py < IconSize; py++) {
        for (let px = 0; px < IconSize; px++) {
          const i = (py * IconSize + px) * 4;
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a === 0) continue;
          // Recolor: red pixels → team color
          if (r > 150 && g < 95 && b < 100) {
            data[i] = cr;
            data[i + 1] = cg;
            data[i + 2] = cb;
            // Shadow removal: near-black pixels in bottom 15% only
          } else if (py >= shadowStartY && r < 40 && g < 40 && b < 40) {
            data[i + 3] = 0;
          }
        }
      }
      tempCtx.putImageData(imageData, 0, 0);

      // Step 2: apply rotation transforms and draw the processed cell
      if (x === 1 || x === 2) {
        ctx.translate(IconSize / 2, IconSize / 2);
        ctx.rotate((-90 * Math.PI) / 180);
        ctx.translate(-IconSize / 2, -IconSize / 2);
      }
      if (x === 2) {
        ctx.scale(-1, 1);
        ctx.translate(-IconSize, 0);
      }
      ctx.drawImage(tempCanvas, 0, 0);
    };

    img.onerror = () => {
      setFailed(true);
      onErrorRef.current?.();
    };
  }, [careerId, x, color]);

  if (failed) return null;

  return (
    <Wrapper size={size} isInjured={x} className="playerIcon" st={st}>
      <Canvas ref={canvasRef} />
    </Wrapper>
  );
}

export default IconPlayerExternal;
